import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export const SEARCH_MIN_LENGTH = 3;

type ListSearchStoreType = {
  isActive: boolean;
  query: string;
  manuallyCollapsed: string[];

  activate: () => void;
  deactivate: () => void;
  setQuery: (query: string) => void;
  toggleManualCollapse: (layerId: string) => void;
  reset: () => void;
};

const useListSearch = create<ListSearchStoreType>((set, get) => ({
  isActive: false,
  query: '',
  manuallyCollapsed: [],

  activate: () => {
    set({ isActive: true, query: '', manuallyCollapsed: [] });
  },

  deactivate: () => {
    if (!get().isActive && get().query === '') return;
    set({ isActive: false, query: '', manuallyCollapsed: [] });
  },

  setQuery: (query: string) => {
    set({ query, manuallyCollapsed: [] });
  },

  toggleManualCollapse: (layerId: string) => {
    set(({ manuallyCollapsed }) => ({
      manuallyCollapsed: manuallyCollapsed.includes(layerId)
        ? manuallyCollapsed.filter((id) => id !== layerId)
        : [...manuallyCollapsed, layerId],
    }));
  },

  reset: () => {
    if (!get().isActive && get().query === '') return;
    set({ isActive: false, query: '', manuallyCollapsed: [] });
  },
}));

const getTrimmedQuery = (query: string) => query.trim();

export const useIsSearchFiltering = () =>
  useListSearch(
    (state) => getTrimmedQuery(state.query).length >= SEARCH_MIN_LENGTH,
  );

export const useIsSearchActive = () => useListSearch((state) => state.isActive);

export const useSearchQuery = () => useListSearch((state) => state.query);

export const useSearchNeedle = () =>
  useListSearch((state) => {
    const trimmed = getTrimmedQuery(state.query);

    return trimmed.length >= SEARCH_MIN_LENGTH ? trimmed.toLowerCase() : '';
  });

export const useIsLayerManuallyCollapsed = (layerId: string) =>
  useListSearch((state) => state.manuallyCollapsed.includes(layerId));

export const useListSearchActions = () =>
  useListSearch(
    useShallow((state) => ({
      activate: state.activate,
      deactivate: state.deactivate,
      setQuery: state.setQuery,
      toggleManualCollapse: state.toggleManualCollapse,
      reset: state.reset,
    })),
  );

export const { getState: getListSearchState } = useListSearch;

export default useListSearch;
